import React, { useEffect, useState, useMemo } from "react";
import TableSkeleton from "../../../../../../../../component/TableSkeleton";
import { Table, TableHead, TableBody, TableRow, TableCell } from "@mui/material";
import { useOutletContext, useNavigate } from "react-router";
import EmptyState from "../../../../../../../../component/EmptyState";
import { StatusChip, headCellSx, bodyCellSx } from "../../../../../../../../component/listing";
import { getAllTimesheets } from "../../../../../../../../services/timesheet";
import { tokens } from "../../../../../../../../theme";
import RecordStat, { RecordStatRow } from "../RecordStat";

const TONE = { incomplete: "muted", pending: "warning", approved: "success", rejected: "danger" };
const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-GB") : "—");

const ProfileTimesheets = () => {
  const [, employeeId] = useOutletContext();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!employeeId) return;
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const res = await getAllTimesheets();
        const list = (Array.isArray(res?.data) ? res.data : []).filter((t) => t.employee === employeeId);
        if (active) setRows(list);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [employeeId]);

  /**
   * Hours, and how much of them was billable. The book leads this panel with
   * these because a list of weeks does not answer "is this person's time
   * being charged", which is the question the panel exists for.
   */
  const stats = useMemo(() => {
    const submitted = rows.filter((r) => r.status && r.status !== "incomplete");
    const hours = submitted.reduce((t, r) => t + (Number(r.totalHours) || 0), 0);
    const overtime = submitted.reduce((t, r) => t + (Number(r.overtimeHours) || 0), 0);
    const billable = submitted.reduce(
      (t, r) => t + (r.activities || []).reduce((h, a) => h + (a?.billable === false ? 0 : Number(a?.hours) || 0), 0),
      0
    );
    const awaiting = rows.filter((r) => r.status === "pending").length;
    return {
      weeks: submitted.length,
      hours,
      overtime,
      awaiting,
      // A share of nothing is not zero percent — that would read as "none of
      // it was billable" rather than "nothing has been logged".
      billableShare: hours > 0 ? billable / hours : null,
      billable,
    };
  }, [rows]);

  if (loading) return (
          /* The header and the column widths are known before the data
             is, so they render immediately and nothing shifts when the
             rows land. */
          <Table sx={{ width: "100%", tableLayout: "fixed" }}>
            <TableHead>
        <TableRow>
          <TableCell sx={headCellSx}>Period</TableCell>
          <TableCell sx={headCellSx}>Total Hours</TableCell>
          <TableCell sx={headCellSx}>Activities</TableCell>
          <TableCell sx={headCellSx}>Status</TableCell>
        </TableRow>
      </TableHead>
            <TableBody><TableSkeleton columns={4} /></TableBody>
          </Table>
        );
  if (rows.length === 0) return <EmptyState title="No timesheets" description="This employee hasn't submitted any timesheets." />;

  return (
    <>
      <RecordStatRow>
        <RecordStat label="Hours submitted" value={stats.hours.toFixed(1)} sub={`across ${stats.weeks} week${stats.weeks === 1 ? "" : "s"}`} />
        <RecordStat label="Overtime" value={stats.overtime.toFixed(1)} sub={stats.overtime > 0 ? "above the standard week" : "none logged"} tone={stats.overtime > 0 ? tokens.warningText : undefined} />
        <RecordStat label="Awaiting approval" value={stats.awaiting} sub={stats.awaiting ? "with their manager" : "nothing pending"} tone={stats.awaiting ? tokens.warningText : undefined} />
        <RecordStat label="Billable" value={stats.billableShare == null ? "—" : `${Math.round(stats.billableShare * 100)}%`} sub={stats.billableShare == null ? "nothing logged yet" : `${stats.billable.toFixed(1)} of ${stats.hours.toFixed(1)} hours`} />
      </RecordStatRow>
    <Table sx={{ width: "100%", tableLayout: "fixed" }}>
      <TableHead>
        <TableRow>
          <TableCell sx={headCellSx}>Period</TableCell>
          <TableCell sx={headCellSx}>Total Hours</TableCell>
          <TableCell sx={headCellSx}>Activities</TableCell>
          <TableCell sx={headCellSx}>Status</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r._id} hover onClick={() => navigate(`/app/timesheet/${r._id}`)}
            sx={{ cursor: "pointer", "&:hover": { background: tokens.surfaceSubtle } }}>
            <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{fmt(r.startDate)} – {fmt(r.endDate)}</TableCell>
            <TableCell sx={bodyCellSx}>{r.totalHours || 0} h</TableCell>
            <TableCell sx={{ ...bodyCellSx, color: tokens.textMuted }}>{r.activities?.length || 0}</TableCell>
            <TableCell sx={bodyCellSx}><StatusChip label={r.status} tone={TONE[r.status]} /></TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
    </>
  );
};

export default ProfileTimesheets;
